"use client"

import { useState, useEffect, useCallback } from "react"
import axios from "axios"
import {
  AppBar,
  Toolbar,
  Typography,
  Box,
  Grid,
  Card,
  Checkbox,
  BottomNavigation,
  BottomNavigationAction,
  Avatar,
  Snackbar,
  Alert,
  IconButton,
} from "@mui/material"
import {
  Home as HomeIcon,
  CalendarToday as CalendarTodayIcon,
  Check as CheckIcon,
  Close as CloseIcon,
  Checklist as ChecklistIcon,
  Notifications as NotificationsIcon,
  AccountCircle as AccountCircleIcon,
  ExitToApp as ExitToAppIcon,
} from "@mui/icons-material"
import { useNavigate } from "react-router-dom"

const baseUrl = process.env.REACT_APP_APP_URL

const InstructorChecklist = () => {
  const navigate = useNavigate()
  const [users, setUsers] = useState([])
  const [seances, setSeances] = useState([])
  const [error, setError] = useState(null)
  const [openSnackbar, setOpenSnackbar] = useState(false)
  const [value, setValue] = useState(0)
  const [loading, setLoading] = useState({}) // Track loading state per user
  const [disabledUsers, setDisabledUsers] = useState(false) // Change from an object to a boolean

  const token = localStorage.getItem("CC_Token")
  const user = JSON.parse(localStorage.getItem("user"))

  const handleCloseSnackbar = () => {
    setOpenSnackbar(false)
  }

  const handleNavigateToNotifications = () => {
    navigate("/AdminGetNotifs")
  }

  const handleProfile = () => {
    user.role == "admin" ? navigate("/ProfileAdmin") : navigate("/Profile") // Redirection vers la page de profil
  }

  const handleLogout = () => {
    localStorage.removeItem("CC_Token")
    localStorage.removeItem("user")
    navigate("/login")
  }

  const getTodayDate = useCallback(() => {
    const now = new Date()
    // Format the date in YYYY-MM-DD format using local time
    const year = now.getFullYear()
    const month = String(now.getMonth() + 1).padStart(2, "0")
    const day = String(now.getDate()).padStart(2, "0")
    const formattedDate = `${year}-${month}-${day}`
    return formattedDate
  }, [])

  const isUserPresent = useCallback(
    (userId) => {
      const todayDate = getTodayDate()
      return seances.some((seance) => seance.user_id === userId && seance.time && seance.time.startsWith(todayDate))
    },
    [seances, getTodayDate],
  )

  const fetchData = useCallback(async () => {
    try {
      const [usersResponse, seancesResponse] = await Promise.all([
        axios.get(`${baseUrl}/api/users`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
        axios.get(`${baseUrl}/api/seances`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
      ])

      if (Array.isArray(usersResponse.data)) {
        // Filter users based on role and coach_id
        const memberUsers = usersResponse.data.filter(
          (user) =>
            user.role === "member" && // Ensure the user is a member
            user.coach_id === getCurrentCoachId(), // Ensure the user is assigned to the current coach
        )
        setUsers(memberUsers)
      } else {
        throw new Error("Invalid users data format")
      }

      if (Array.isArray(seancesResponse.data)) {
        setSeances(seancesResponse.data)
        console.log("Seances data:", seancesResponse.data)
      } else {
        throw new Error("Invalid seances data format")
      }
    } catch (err) {
      console.error("Error fetching data:", err)
      setError("Erreur lors de la récupération des données")
      setOpenSnackbar(true)
      setUsers([])
      setSeances([])
    }
  }, [token])

  // Helper function to get the current coach's ID from localStorage
  const getCurrentCoachId = () => {
    const userString = localStorage.getItem("user")
    if (userString) {
      try {
        const userData = JSON.parse(userString)
        return Number.parseInt(userData.id) // Ensure the ID is parsed as an integer
      } catch (error) {
        console.error("Error parsing user data:", error)
        return 0 // Return 0 or another default value if parsing fails
      }
    }
    return 0 // Return 0 or another default value if no user is found
  }

  useEffect(() => {
    fetchData()
  }, [fetchData])

  const toggleUserStatus = async (userId) => {
    // Set loading and disabled states for this user
    setLoading((prev) => ({ ...prev, [userId]: true }))
    setDisabledUsers(true) // Disable all checkboxes

    const todayDate = getTodayDate()
    const userPresent = isUserPresent(userId)

    try {
      if (!userPresent) {
        // Optimistically update UI
        const now = new Date()
        const timeString = `${todayDate}T${String(now.getHours()).padStart(2, "0")}:${String(now.getMinutes()).padStart(2, "0")}:${String(now.getSeconds()).padStart(2, "0")}`

        setSeances((prev) => [...prev, { user_id: userId, time: timeString, id: "temp" }])

        const response = await axios.post(
          `${baseUrl}/api/seances`,
          { date: todayDate, user_id: userId },
          { headers: { Authorization: `Bearer ${token}` } },
        )

        // Update with actual data from server
        setSeances((prev) => prev.map((seance) => (seance.id === "temp" ? { ...response.data } : seance)))
      } else {
        const seanceToDelete = seances.find(
          (seance) => seance.user_id === userId && seance.time && seance.time.startsWith(todayDate),
        )

        if (seanceToDelete) {
          // Optimistically remove from UI
          setSeances((prev) => prev.filter((seance) => seance.id !== seanceToDelete.id))

          await axios.delete(`${baseUrl}/api/seances/${seanceToDelete.id}`, {
            headers: { Authorization: `Bearer ${token}` },
          })
        }
      }
    } catch (err) {
      console.error("Error updating status:", err)
      setError("Erreur lors de la mise à jour du statut")
      setOpenSnackbar(true)
      // Revert optimistic update on error
      fetchData()
    } finally {
      setLoading((prev) => ({ ...prev, [userId]: false }))

      // Set a timeout to re-enable the checkbox after 3 seconds
      setTimeout(() => {
        setDisabledUsers(false) // Now it correctly updates as a boolean
      }, 3000)
    }
  }

  return (
    <div className="app">
      {/* En-tête */}
      <AppBar position="fixed" sx={{ backgroundColor: "#003366" }}>
        <Toolbar>
          <Avatar alt="Logo" src={"../assets/cej.png"} sx={{ width: 40, height: 40, marginRight: "0.5rem" }} />
          <Typography
            variant="h6"
            sx={{
              flexGrow: 1,
              fontSize: { xs: "1rem", sm: "1.25rem" },
              color: "#ffffff",
            }}
          >
            Club Équestre Jafoura
          </Typography>
          <IconButton color="inherit" onClick={handleNavigateToNotifications}>
            <NotificationsIcon />
          </IconButton>
          <IconButton color="inherit" onClick={handleProfile}>
            <AccountCircleIcon />
          </IconButton>
          <IconButton color="inherit" onClick={handleLogout}>
            <ExitToAppIcon />
          </IconButton>
        </Toolbar>
      </AppBar>

      {/* Contenu principal */}
      <Box
        sx={{
          padding: "1rem",
          paddingTop: "70px",
          paddingBottom: "70px",
          backgroundColor: "#f5f5f5",
          minHeight: "100vh",
        }}
      >
        {/* Titre "Appel des Cavaliers" */}
        <Typography
          variant="h5"
          sx={{
            fontWeight: "bold",
            color: "#003366",
            textAlign: "center",
            marginBottom: "2rem",
          }}
        >
          Appel des Cavaliers
        </Typography>

        {/* Liste des cavaliers */}
        <Grid container spacing={2}>
          {users.map((user) => (
            <Grid item xs={12} sm={6} md={4} key={user.id}>
              <Card
                sx={{
                  display: "flex",
                  alignItems: "center",
                  padding: "1rem",
                  backgroundColor: isUserPresent(user.id) ? "#e8f5e9" : "#fff3e0",
                  transition: "background-color 0.3s ease, transform 0.2s ease",
                  "&:hover": {
                    transform: "scale(1.02)",
                    boxShadow: 3,
                  },
                }}
              >
                <Avatar alt={user.name} src={user.avatar} sx={{ width: 56, height: 56, marginRight: "1rem" }} />
                <Box sx={{ flexGrow: 1 }}>
                  <Typography
                    sx={{
                      fontWeight: 500,
                      color: "#333",
                      fontSize: "1rem",
                    }}
                  >
                    {user.name}
                  </Typography>
                  <Box sx={{ display: "flex", alignItems: "center", mt: 1 }}>
                    {isUserPresent(user.id) ? (
                      <CheckIcon sx={{ color: "#4caf50", fontSize: 20, mr: 1 }} />
                    ) : (
                      <CloseIcon sx={{ color: "#f57c00", fontSize: 20, mr: 1 }} />
                    )}
                    <Typography
                      sx={{
                        color: isUserPresent(user.id) ? "#4caf50" : "#f57c00",
                        fontSize: "0.875rem",
                      }}
                    >
                      {isUserPresent(user.id) ? "Présent" : "Absent"}
                    </Typography>
                  </Box>
                </Box>
                <Checkbox
                  checked={isUserPresent(user.id)}
                  onChange={() => toggleUserStatus(user.id)}
                  disabled={loading[user.id] || disabledUsers}
                  sx={{
                    color: "#f57c00",
                    "&.Mui-checked": {
                      color: "#f57c00",
                    },
                    "& .MuiSvgIcon-root": {
                      fontSize: 28,
                    },
                  }}
                />
              </Card>
            </Grid>
          ))}
        </Grid>
      </Box>

      {/* Pied de page */}
      <Box sx={{ position: "fixed", bottom: 0, width: "100%" }}>
        <BottomNavigation
          sx={{
            backgroundColor: "#003366",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <BottomNavigationAction
            label="Calendrier"
            icon={<CalendarTodayIcon sx={{ fontSize: 30, color: "#ffffff" }} />}
            sx={{ color: "#ffffff", minWidth: "auto" }}
            onClick={() => navigate("/AdminChrono")}
          />
          <BottomNavigationAction
            label="Home"
            icon={<HomeIcon sx={{ fontSize: 30, color: "#ffffff" }} />}
            sx={{ color: "#ffffff", minWidth: "auto" }}
            onClick={() => navigate("/InstructorHome")}
          />
          <BottomNavigationAction
            label="Checklist"
            icon={<ChecklistIcon sx={{ fontSize: 30, color: "#ffffff" }} />}
            sx={{ color: "#ffffff", minWidth: "auto" }}
            onClick={() => navigate("/InstructorChecklist")}
          />
        </BottomNavigation>
      </Box>

      {/* Snackbar pour les erreurs */}
      <Snackbar
        open={openSnackbar}
        autoHideDuration={3000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity="error"
          sx={{
            width: "100%",
            backgroundColor: "#f44336",
            color: "white",
            "& .MuiAlert-icon": {
              color: "white",
            },
          }}
        >
          {error}
        </Alert>
      </Snackbar>
    </div>
  )
}

export default InstructorChecklist

